import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  COACH_COLUMNS,
  COACH_STATUS_OPTIONS,
  DEFAULT_PAGE_SIZE,
  ONE,
  ZERO,
} from '@/constants';
import {
  Button,
  PageHeaderLayout,
  Pagination,
  Search,
  Table,
  TableContainer,
} from '@/ui';

import type { CoachStatus } from '@/constants/coachStatus';
import type { CoachSortBy } from '@/constants/coaches';
import type { ManageCoach } from '@/store/api/userApi';
import type { SortDirection } from '@/types';
import type { ChangeEvent } from 'react';

import List from '@/components/Coaches/List';
import Header from '@/components/Coaches/List/Header';
import Modal from '@/components/Coaches/New/modal';
import { compareCoachStatuses } from '@/constants/coachStatus';
import FilterDropdown from '@/features/membersTable/FilterDropdown';
import CapacityFilter from '@/pages/Manage/CapacityFilter';
import DateRangeFilter from '@/pages/Manage/DateRangeFilter';
import NumberRangeFilter from '@/pages/Manage/NumberRangeFilter';
import { getMockExtras, setMockExtras } from '@/pages/Manage/mockExtrasRef';
import { useGetManageCoachesQuery } from '@/store/api/userApi';

type NumberRange = [number | undefined, number | undefined];

const IS_LOCAL = import.meta.env.VITE_APP_ENV === 'local';

const MOCK_ONLY_SORT_KEYS = new Set<string>([
  'active_chem_calls',
  'rate',
  'utilization',
]);

const columns = IS_LOCAL
  ? COACH_COLUMNS
  : COACH_COLUMNS.map((col) =>
      MOCK_ONLY_SORT_KEYS.has(col.key) ? { ...col, sortable: false } : col,
    );

export const manageCoachesQueryArgs = ({
  capacityFilter,
  membersFilter,
  page,
  search,
  sortBy,
  sortDirection,
  statusFilter,
}: {
  capacityFilter: NumberRange;
  membersFilter: NumberRange;
  page: number;
  search: string;
  sortBy: CoachSortBy;
  sortDirection: SortDirection;
  statusFilter: CoachStatus[];
}) => ({
  capacityMax: capacityFilter[1],
  capacityMin: capacityFilter[0],
  coach: true,
  coachStatuses: statusFilter.length > 0 ? statusFilter : undefined,
  limit: DEFAULT_PAGE_SIZE,
  membersMax: membersFilter[1],
  membersMin: membersFilter[0],
  page,
  search,
  sortBy,
  sortDirection,
});

const getExtras = (id: string) =>
  getMockExtras().get(id) ?? {
    activeChemCalls: 0,
    executiveRate: 0,
    members: 0,
    standardRate: 0,
  };

export const sortLocalCoaches = (
  coaches: ManageCoach[],
  sortBy: CoachSortBy,
  sortDirection: SortDirection,
): ManageCoach[] => {
  if (sortBy === 'coach_status') {
    return [...coaches].sort((a, b) =>
      compareCoachStatuses(
        a.coachStatus,
        b.coachStatus,
        sortDirection,
        a.id,
        b.id,
      ),
    );
  }

  const sorted = [...coaches].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';
    switch (sortBy) {
      case 'first_name':
        aVal = a.fullName.toLowerCase();
        bVal = b.fullName.toLowerCase();
        break;
      case 'focus':
        aVal = (a.coachProfile?.focus ?? '').toLowerCase();
        bVal = (b.coachProfile?.focus ?? '').toLowerCase();
        break;
      case 'email':
        aVal = a.email.toLowerCase();
        bVal = b.email.toLowerCase();
        break;
      case 'active_chem_calls':
        aVal = getExtras(a.id).activeChemCalls;
        bVal = getExtras(b.id).activeChemCalls;
        break;
      case 'members':
        aVal = getExtras(a.id).members;
        bVal = getExtras(b.id).members;
        break;
      case 'capacity':
        aVal = a.coachProfile?.capacity ?? 0;
        bVal = b.coachProfile?.capacity ?? 0;
        break;
      case 'utilization': {
        const aCap = a.coachProfile?.capacity ?? 0;
        const bCap = b.coachProfile?.capacity ?? 0;
        aVal = aCap > 0 ? getExtras(a.id).members / aCap : 0;
        bVal = bCap > 0 ? getExtras(b.id).members / bCap : 0;
        break;
      }
      case 'rate':
        aVal = getExtras(a.id).standardRate;
        bVal = getExtras(b.id).standardRate;
        break;
      case 'start_date':
        aVal = a.coachProfile?.startDate ?? '';
        bVal = b.coachProfile?.startDate ?? '';
        break;
      case 'updated_at':
        aVal = a.updatedAt;
        bVal = b.updatedAt;
        break;
      default:
        break;
    }
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
  return sortDirection === 'desc' ? sorted.reverse() : sorted;
};

const Coaches = () => {
  const [page, setPage] = useState<number>(ONE);
  const [search, setSearch] = useState('');
  const [newCoach, setNewCoach] = useState(false);
  const [sortBy, setSortBy] = useState<CoachSortBy>('first_name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [statusFilter, setStatusFilter] = useState<CoachStatus[]>([]);
  const [capacityFilter, setCapacityFilter] = useState<NumberRange>([
    undefined,
    undefined,
  ]);
  const [capacityRange, setCapacityRange] = useState<[number, number]>([0, 10]);
  const [maxCapacity, setMaxCapacity] = useState(10);
  const [membersFilter, setMembersFilter] = useState<NumberRange>([
    undefined,
    undefined,
  ]);
  const [membersRange, setMembersRange] = useState<[number, number]>([0, 10]);
  const [maxMembers, setMaxMembers] = useState(10);
  const [chemCallsRange, setChemCallsRange] = useState<[number, number]>([
    0, 10,
  ]);
  const [maxChemCalls, setMaxChemCalls] = useState(10);
  const [startDateRange, setStartDateRange] = useState<[string, string]>([
    '',
    '',
  ]);
  const [mockCoaches, setMockCoaches] = useState<ManageCoach[]>([]);
  const [mockLoading, setMockLoading] = useState(IS_LOCAL);

  useEffect(() => {
    if (IS_LOCAL) {
      import('@/pages/Manage/mockCoaches')
        .then((m) => {
          setMockExtras(m.COACH_EXTRAS_MAP);
          setMockCoaches(m.MOCK_COACHES);
          const maxCap = Math.max(
            ...m.MOCK_COACHES.map((c) => c.coachProfile?.capacity ?? 0),
          );
          const maxMem = Math.max(
            ...m.MOCK_COACHES.map(
              (c) => m.COACH_EXTRAS_MAP.get(c.id)?.members ?? 0,
            ),
          );
          setMaxCapacity(maxCap);
          setCapacityRange([0, maxCap]);
          setMaxMembers(maxMem);
          setMembersRange([0, maxMem]);
          const maxCC = Math.max(
            ...m.MOCK_COACHES.map(
              (c) => m.COACH_EXTRAS_MAP.get(c.id)?.activeChemCalls ?? 0,
            ),
          );
          setMaxChemCalls(maxCC);
          setChemCallsRange([0, maxCC]);
        })
        .catch(() => {
          // If the mock chunk fails to load, fall through to an empty table
          // rather than leaving the spinner up forever.
        })
        .finally(() => {
          setMockLoading(false);
        });
    }
  }, []);

  const { data, isLoading } = useGetManageCoachesQuery(
    IS_LOCAL
      ? { coach: true, limit: 1, page: 1 }
      : manageCoachesQueryArgs({
          capacityFilter,
          membersFilter,
          page,
          search,
          sortBy,
          sortDirection,
          statusFilter,
        }),
  );

  const allCoaches = IS_LOCAL ? mockCoaches : data?.items || [];

  const prevMaxCapRef = useRef(maxCapacity);
  useEffect(() => {
    if (!IS_LOCAL && allCoaches.length > 0) {
      const maxCap = Math.max(
        ...allCoaches.map((c) => c.coachProfile?.capacity ?? 0),
      );
      if (maxCap !== prevMaxCapRef.current) {
        prevMaxCapRef.current = maxCap;
        setMaxCapacity(maxCap);
        setCapacityRange([0, maxCap]);
      }
    }
  }, [allCoaches]);

  // Client-side filtering only applies in local mock mode.
  // In production the API handles filtering/pagination.
  const processedCoaches = useMemo(() => {
    if (!IS_LOCAL) return allCoaches;

    let result = allCoaches;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.fullName.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          (c.coachProfile?.focus ?? '').toLowerCase().includes(q),
      );
    }

    if (statusFilter.length > 0) {
      result = result.filter((c) =>
        (c.coachStatuses ?? (c.coachStatus ? [c.coachStatus] : [])).some(
          (status) => statusFilter.includes(status),
        ),
      );
    }

    if (capacityRange[0] > 0 || capacityRange[1] < maxCapacity) {
      result = result.filter((c) => {
        const cap = c.coachProfile?.capacity ?? 0;
        return cap >= capacityRange[0] && cap <= capacityRange[1];
      });
    }

    if (membersRange[0] > 0 || membersRange[1] < maxMembers) {
      result = result.filter((c) => {
        const mem = getExtras(c.id).members;
        return mem >= membersRange[0] && mem <= membersRange[1];
      });
    }

    if (chemCallsRange[0] > 0 || chemCallsRange[1] < maxChemCalls) {
      result = result.filter((c) => {
        const cc = getExtras(c.id).activeChemCalls;
        return cc >= chemCallsRange[0] && cc <= chemCallsRange[1];
      });
    }

    if (startDateRange[0] || startDateRange[1]) {
      result = result.filter((c) => {
        // startDate may be `YYYY-MM-DD` or an ISO timestamp; compare on the
        // date portion only so an ISO time-of-day doesn't push a coach past
        // the selected end date.
        const sd = (c.coachProfile?.startDate ?? '').slice(0, 10);
        if (!sd) return false;
        if (startDateRange[0] && sd < startDateRange[0]) return false;
        if (startDateRange[1] && sd > startDateRange[1]) return false;
        return true;
      });
    }

    return sortLocalCoaches(result, sortBy, sortDirection);
  }, [
    allCoaches,
    search,
    statusFilter,
    capacityRange,
    maxCapacity,
    membersRange,
    maxMembers,
    chemCallsRange,
    maxChemCalls,
    startDateRange,
    sortBy,
    sortDirection,
  ]);

  const totalPages = IS_LOCAL
    ? Math.ceil(processedCoaches.length / DEFAULT_PAGE_SIZE)
    : (data?.pages ?? ZERO);

  const pagedCoaches = IS_LOCAL
    ? processedCoaches.slice(
        (page - 1) * DEFAULT_PAGE_SIZE,
        page * DEFAULT_PAGE_SIZE,
      )
    : processedCoaches;

  useEffect(() => {
    if (!IS_LOCAL) return;
    setPage(ONE);
  }, [
    statusFilter,
    capacityRange,
    membersRange,
    chemCallsRange,
    startDateRange,
  ]);

  const onAddCoach = () => setNewCoach(true);
  const onCloseAddCoach = () => setNewCoach(false);
  const onPageChange = (_: ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const onSort = useCallback(
    (field: CoachSortBy) => {
      if (!IS_LOCAL && MOCK_ONLY_SORT_KEYS.has(field)) return;

      if (field === sortBy) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setSortDirection('asc');
      }
      setPage(ONE);
    },
    [sortBy],
  );

  const onSearchChange = useCallback((value: string) => {
    setPage(ONE);
    setSearch(value);
  }, []);

  const onStatusFilterChange = useCallback((selected: CoachStatus[]) => {
    setPage(ONE);
    setStatusFilter(selected);
  }, []);

  const onMembersFilterChange = useCallback((value: NumberRange) => {
    setPage(ONE);
    setMembersFilter(value);
  }, []);

  const onCapacityFilterChange = useCallback((value: NumberRange) => {
    setPage(ONE);
    setCapacityFilter(value);
  }, []);

  const hasActiveFilters =
    statusFilter.length > 0 ||
    search.length > 0 ||
    capacityRange[0] > 0 ||
    capacityRange[1] < maxCapacity ||
    capacityFilter[0] !== undefined ||
    capacityFilter[1] !== undefined ||
    membersRange[0] > 0 ||
    membersRange[1] < maxMembers ||
    membersFilter[0] !== undefined ||
    membersFilter[1] !== undefined ||
    chemCallsRange[0] > 0 ||
    chemCallsRange[1] < maxChemCalls ||
    startDateRange[0] !== '' ||
    startDateRange[1] !== '';

  const onClearAll = () => {
    setPage(ONE);
    setStatusFilter([]);
    setCapacityFilter([undefined, undefined]);
    setCapacityRange([0, maxCapacity]);
    setMembersFilter([undefined, undefined]);
    setMembersRange([0, maxMembers]);
    setChemCallsRange([0, maxChemCalls]);
    setStartDateRange(['', '']);
    setSearch('');
  };

  return (
    <div className="w-full p-4">
      <PageHeaderLayout title="Coaches">
        <Button variant="contained" onClick={onAddCoach}>
          Add coach
        </Button>
      </PageHeaderLayout>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Search
          className="w-full md:w-1/3"
          search={search}
          setSearch={onSearchChange}
        />
        <FilterDropdown
          label="Status"
          options={COACH_STATUS_OPTIONS}
          selected={statusFilter}
          onChange={(selected) =>
            onStatusFilterChange(selected as CoachStatus[])
          }
        />
        {IS_LOCAL ? (
          <>
            <CapacityFilter
              label="Active CC"
              max={maxChemCalls}
              value={chemCallsRange}
              onChange={setChemCallsRange}
            />
            <CapacityFilter
              label="Members"
              max={maxMembers}
              value={membersRange}
              onChange={setMembersRange}
            />
            <CapacityFilter
              max={maxCapacity}
              value={capacityRange}
              onChange={setCapacityRange}
            />
            <DateRangeFilter
              value={startDateRange}
              onChange={setStartDateRange}
            />
          </>
        ) : (
          <>
            <NumberRangeFilter
              label="Members"
              value={membersFilter}
              onChange={onMembersFilterChange}
            />
            <NumberRangeFilter
              label="Capacity"
              value={capacityFilter}
              onChange={onCapacityFilterChange}
            />
          </>
        )}
        {hasActiveFilters && (
          <button
            className="hover:text-cement-700 text-xs text-cement-500 underline"
            type="button"
            onClick={onClearAll}
          >
            Clear all
          </button>
        )}
      </div>

      <div className="flex flex-1 flex-col pt-4">
        <TableContainer>
          <Table aria-label="coaches table">
            <Header
              columns={columns}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={onSort}
            />
            <List
              coaches={pagedCoaches}
              isLoading={IS_LOCAL ? mockLoading : isLoading}
            />
          </Table>
        </TableContainer>
        <Pagination count={totalPages} page={page} onChange={onPageChange} />
      </div>
      {newCoach && <Modal onClose={onCloseAddCoach} />}
    </div>
  );
};

export default Coaches;
